import { useEffect, useRef } from 'react'
import RowIconCluster from './RowIconCluster'
import { TEMPLATE_COLUMNS } from './templateColumns'

// Manage Templates' row radio (letter 106, R4a; 01.10 export #templates-pop-up): a real control, one
// template at a time, the one Update Template overwrites. White, 1px #C6CFC6 -> #4CBC18 when selected
// or hovered (as the checkbox, letter 104 1.1.1), with a 10px #4AA52A dot.
// One Tab stop for the group (`tabbable`), arrows move the selection (`onArrow`), as a radiogroup does.
function SelectRadio({ selected, onSelect, label, tabbable, onArrow }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      tabIndex={tabbable ? 0 : -1}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) return
        e.preventDefault()
        onArrow?.(e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1)
      }}
      className={`w-[20px] h-[20px] p-0 rounded-full shrink-0 flex items-center justify-center bg-white border ${
        selected ? 'border-brand-green' : 'border-[#C6CFC6] hover:border-brand-green'
      }`}
    >
      {selected && <span className="w-[10px] h-[10px] rounded-full bg-[#4AA52A]" />}
    </button>
  )
}

// A Manage Templates row (letter 106 R4; 01.10 export #templates-pop-up), or Save as New Template's draft.
export default function TemplateRow({
  template,
  checked,
  onToggleChecked,
  isEditing,
  editValue,
  onEditValueChange,
  onCommitEdit,
  onCancelEdit,
  onEdit,
  onDelete,
  deletable,
  deleteTooltip,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  lastModifiedLabel,
  radioTabbable = true,
  onRadioArrow,
  draft = false,
}) {
  // Focus with the name selected, so typing replaces it (letter 99, 2.5: the user edits the name).
  // An effect, not autoFocus + onFocus: the browser puts the caret at the end after autoFocus.
  const inputRef = useRef(null)
  useEffect(() => {
    if (!isEditing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  const renameInput = (
    <input
      ref={inputRef}
      aria-label="Template name"
      // The server's own limit (reilm-be modules/users/flipTemplateFields.js, MAX_NAME_LENGTH), so a
      // long name stops at the field instead of coming back as a technical error line.
      maxLength={120}
      value={editValue}
      onChange={(e) => onEditValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommitEdit()
        if (e.key === 'Escape') onCancelEdit()
      }}
      // w-full, capped at the spec's own 188 rather than a bare fixed width -- at 1280 the
      // container is wider than 188 so the cap binds and S8.1's drawn size is unchanged; at
      // 375 it shrinks with the container instead of overflowing it (fix round 23.09.2026).
      className="w-full max-w-[188px] h-[36px] rounded-[4px] px-[12px] text-[14px] text-brand-dark outline-none"
      style={{ border: '1px solid #4CBC18' }}
    />
  )

  const iconCluster = (
    <RowIconCluster
      onEdit={onEdit}
      onDelete={onDelete}
      deletable={deletable}
      deleteTooltip={deleteTooltip}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
    />
  )

  // Save as New Template's draft row (letter 99, 2.5): the name field where the new row will land,
  // an empty radio for alignment, no date and no icons until the template exists.
  if (draft) {
    return (
      <div className="h-[64px] flex items-center gap-x-[8px] border-b border-brand-fieldBdr" data-testid="draft-template-row">
        <span aria-hidden="true" className="w-[20px] h-[20px] rounded-full shrink-0 bg-white border border-[#C6CFC6]" />
        <div className="flex-1 min-w-0 pr-[8px]">{renameInput}</div>
      </div>
    )
  }

  // Manage Templates (letter 106, R4a; 01.10 export): radio 20 at the left edge, the name 8 after it
  // as plain text (letter 99, 2.11); date right-aligned 14/400 #17270D; every row, the last included,
  // carries the 1px #C6CFC6 rule. Rename only through the edit icon.
  return (
    <div className="h-[64px] flex items-center gap-x-[8px] border-b border-brand-fieldBdr">
      <SelectRadio
        selected={checked}
        onSelect={onToggleChecked}
        label={`Select ${template.template_name}`}
        tabbable={radioTabbable}
        onArrow={onRadioArrow}
      />
      <div className="flex-1 min-w-0 pr-[8px]">
        {isEditing ? (
          renameInput
        ) : (
          <span title={template.template_name} className="block truncate text-[14px] text-brand-dark">
            {template.template_name}
          </span>
        )}
      </div>
      <div className={`${TEMPLATE_COLUMNS.manageDate} shrink-0 text-right text-[14px] font-normal text-brand-dark`}>
        {lastModifiedLabel}
      </div>
      <div className={`${TEMPLATE_COLUMNS.manageIconGap} shrink-0`}>{iconCluster}</div>
    </div>
  )
}
